// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useState } from 'react';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { AppLayout, Flashbar } from '@awsui/components-react';

import { Logger } from 'aws-amplify';

import { CallsContext } from '../../contexts/calls';

import useNotifications from '../../hooks/use-notifications';
import useSplitPanel from '../../hooks/use-split-panel';
import useCallsGraphQlApi from '../../hooks/use-calls-graphql-api';

import CallList from '../call-list';
import CallDetails from '../call-details';
import { appLayoutLabels } from '../common/labels';

import Navigation from './navigation';
import Breadcrumbs from './breadcrumbs';
import ToolsPanel from './tools-panel';
import SplitPanel from './calls-split-panel';

import { CALL_LIST_SHARDS_PER_DAY, PERIODS_TO_LOAD_STORAGE_KEY } from '../call-list/calls-table-config';

import useAppContext from '../../contexts/app';

const logger = new Logger('CallAnalyticsLayout');

const CallAnalyticsLayout = () => {
  const { navigationOpen, setNavigationOpen } = useAppContext();

  const { path } = useRouteMatch();
  logger.debug('path', path);

  const notifications = useNotifications();
  const [toolsOpen, setToolsOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [previousSelectedItems, setPreviousSelectedItems] = useState([]);
  const [fromDismiss, setFromDismiss] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);

  // Custom function to update selected items while preserving previous state
  const updateSelectedItems = (newItems) => {
    // Only update if there's an actual change
    if (JSON.stringify(newItems) !== JSON.stringify(selectedItems)) {
      // Store current items as previous before updating
      setPreviousSelectedItems([...selectedItems]);
      setSelectedItems(newItems);
    }
  };

  // Just use the regular setSelectedItems function
  const getInitialPeriodsToLoad = () => {
    // default to 30 days
    const defaultPeriods = 30 * CALL_LIST_SHARDS_PER_DAY;

    try {
      const storedValue = localStorage.getItem(PERIODS_TO_LOAD_STORAGE_KEY);

      // If no value in storage or it's null, use default
      if (!storedValue) {
        localStorage.setItem(PERIODS_TO_LOAD_STORAGE_KEY, JSON.stringify(defaultPeriods));
        return defaultPeriods;
      }

      const periodsFromStorage = Math.abs(JSON.parse(storedValue));

      // Check if value is valid
      if (
        !Number.isFinite(periodsFromStorage) ||
        !Number.isSafeInteger(periodsFromStorage) ||
        periodsFromStorage <= 0 ||
        periodsFromStorage > CALL_LIST_SHARDS_PER_DAY * 30
      ) {
        logger.warn('invalid initialPeriodsToLoad value from local storage');
        localStorage.setItem(PERIODS_TO_LOAD_STORAGE_KEY, JSON.stringify(defaultPeriods));
        return defaultPeriods;
      }

      return periodsFromStorage;
    } catch (error) {
      logger.warn('failed to parse initialPeriodsToLoad from local storage', error);
      localStorage.setItem(PERIODS_TO_LOAD_STORAGE_KEY, JSON.stringify(defaultPeriods));
      return defaultPeriods;
    }
  };
  const initialPeriodsToLoad = getInitialPeriodsToLoad();

  const {
    calls,
    callTranscriptPerCallId,
    getCallDetailsFromCallIds,
    isCallsListLoading,
    periodsToLoad,
    setLiveTranscriptCallId,
    setIsCallsListLoading,
    setPeriodsToLoad,
    sendGetTranscriptSegmentsRequest,
  } = useCallsGraphQlApi({ initialPeriodsToLoad });

  // eslint-disable-next-line prettier/prettier
  const { splitPanelOpen, onSplitPanelToggle, splitPanelSize, onSplitPanelResize } = useSplitPanel(selectedItems);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const callsContextValue = {
    calls,
    callTranscriptPerCallId,
    getCallDetailsFromCallIds,
    isCallsListLoading,
    selectedItems,
    previousSelectedItems,
    sendGetTranscriptSegmentsRequest,
    setIsCallsListLoading,
    setLiveTranscriptCallId,
    setPeriodsToLoad,
    setToolsOpen,
    setSelectedItems: updateSelectedItems,
    periodsToLoad,
    toolsOpen,
    fromDismiss,
    setFromDismiss,
    confirmModalVisible,
    setConfirmModalVisible,
    chatHistory,
    setChatHistory,
  };

  return (
    <CallsContext.Provider value={callsContextValue}>
      <AppLayout
        headerSelector="#top-navigation"
        navigation={<Navigation />}
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        breadcrumbs={<Breadcrumbs />}
        notifications={<Flashbar items={notifications} />}
        tools={<ToolsPanel />}
        toolsOpen={toolsOpen}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        splitPanelOpen={splitPanelOpen}
        onSplitPanelToggle={onSplitPanelToggle}
        splitPanelSize={splitPanelSize}
        onSplitPanelResize={onSplitPanelResize}
        splitPanel={<SplitPanel />}
        content={
          <Switch>
            <Route exact path={path}>
              <CallList />
            </Route>
            <Route path={`${path}/:callId`}>
              <CallDetails />
            </Route>
          </Switch>
        }
        ariaLabels={appLayoutLabels}
      />
    </CallsContext.Provider>
  );
};

export default CallAnalyticsLayout;
